import React, { useContext, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { GET_BUSINESS_FAXES, GET_BUSINESS_FAX_USAGE } from "../../../graphql/queries";
import { SEND_BUSINESS_FAX } from "../../../graphql/mutations";
import { useSelectedBusiness } from "../../../redux/store";
import { UserContext } from "../../auth/auth-context";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { FeatureAccess, getFeatureAccess } from "../../../util/featureAccess";

const toLocalTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const payload = raw.includes(",") ? raw.split(",")[1] : raw;
      resolve(payload || "");
    };
    reader.onerror = () => reject(new Error("Unable to read selected file."));
    reader.readAsDataURL(file);
  });

export default function BusinessFaxTab({ isDisabled = false }) {
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessId = Number(selectedBusiness?.businessBasic?.id || 0);
  const faxNumber = selectedBusiness?.businessBasic?.faxNumber || "";
  const fileInputRef = useRef(null);
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);

  const faxFeatureAccess = getFeatureAccess(providerUser, FeatureAccess.EFAX);
  const premiumPlanRequired = faxFeatureAccess.requiredPlanId;
  const hasPremiumAccess = faxFeatureAccess.hasAccess;

  const [toNumber, setToNumber] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [subject, setSubject] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);

  const { data, loading, refetch } = useQuery(GET_BUSINESS_FAXES, {
    variables: { businessId, limit: 100 },
    skip: !businessId || !hasPremiumAccess,
    fetchPolicy: "network-only",
  });

const toFriendlyFaxError = (message = "") => {
  const normalized = String(message || "").toLowerCase();
  if (normalized.includes("api credentials") && normalized.includes("invalid")) {
    return "eFax provider credentials are invalid on the server. Update fax API key/secret, then retry.";
  }
  return message || "Unable to send fax.";
};
  const { data: usageData, loading: usageLoading, refetch: refetchUsage } = useQuery(
    GET_BUSINESS_FAX_USAGE,
    {
      variables: { businessId },
      skip: !businessId || !hasPremiumAccess,
      fetchPolicy: "network-only",
    }
  );
  const [sendBusinessFax, { loading: sending }] = useMutation(SEND_BUSINESS_FAX);

  const records = useMemo(() => data?.getBusinessFaxes || [], [data]);
  const usage = usageData?.getBusinessFaxUsage || null;

  const handleSelectFile = () => {
    if (isDisabled) return;
    if (!hasPremiumAccess) {
      setCheckPlanNeeded(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    if (isDisabled) return;
    if (!hasPremiumAccess) {
      setCheckPlanNeeded(true);
      return;
    }

    setError("");
    setUploading(true);
    try {
      const base64Content = await readFileAsBase64(selectedFile);
      if (!base64Content) {
        throw new Error("Selected file is empty.");
      }
      setAttachedFile({
        fileName: selectedFile.name || "fax-document",
        mimeType: selectedFile.type || "application/octet-stream",
        contentBase64: base64Content,
      });
      setFileUrl(`Attached file: ${selectedFile.name || "fax-document"}`);
      if (!fileName.trim()) setFileName(selectedFile.name || "");
    } catch (uploadError) {
      setError(uploadError?.message || "Unable to upload file.");
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const handleSend = async () => {
    if (isDisabled || !businessId) return;
    if (!hasPremiumAccess) {
      setCheckPlanNeeded(true);
      return;
    }
    setError("");
    if (!toNumber.trim()) {
      setError("Destination fax number is required.");
      return;
    }
    const hasAttachedFile = Boolean(attachedFile?.contentBase64);
    const hasFileUrl = Boolean(fileUrl.trim());
    if (!hasAttachedFile && !hasFileUrl) {
      setError("Attach a file or provide a document URL.");
      return;
    }
    try {
      if (hasAttachedFile) {
        const token = localStorage.getItem("token") || "";
        const apiServer = process.env.REACT_APP_API_SERVER_URI || "";
        let apiBase = window.location.origin;
        try {
          apiBase = new URL(apiServer, window.location.origin).origin;
        } catch (_error) {
          apiBase = window.location.origin;
        }

        const response = await fetch(`${apiBase}/api/fax/send-upload`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify({
            businessId,
            toNumber: toNumber.trim(),
            fileName: fileName.trim() || attachedFile.fileName || "fax-document",
            mimeType: attachedFile.mimeType,
            contentBase64: attachedFile.contentBase64,
            subject: subject.trim() || null,
          }),
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body?.ok) {
          throw new Error(body?.error || "Unable to send fax.");
        }
      } else {
        await sendBusinessFax({
          variables: {
            input: {
              businessId,
              toNumber: toNumber.trim(),
              fileUrl: fileUrl.trim(),
              fileName: fileName.trim() || null,
              subject: subject.trim() || null,
            },
          },
        });
      }
      setToNumber("");
      setFileUrl("");
      setFileName("");
      setSubject("");
      setAttachedFile(null);
      refetch();
      refetchUsage();
      } catch (e) {
      setError(toFriendlyFaxError(e?.message));
      }
  };

  return (
    <Box>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="eFax (inbound and outbound)"
          requiredPlanId={premiumPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
      <Typography variant="h6" sx={{ mb: 1 }}>
        eFax (Inbound & Outbound)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send outbound fax and monitor inbound/outbound fax events for this business.
      </Typography>

      {!hasPremiumAccess && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => setCheckPlanNeeded(true)}
            >
              Upgrade Plan
            </Button>
          }
        >
          Premium plan required for inbound and outbound eFax.
        </Alert>
      )}

      {!faxNumber && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Set your business fax number in Business Basic before using outbound fax.
        </Alert>
      )}

      {hasPremiumAccess && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Used This Month (In + Out)
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {usageLoading ? "..." : usage?.used ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Monthly Limit
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {usageLoading
                  ? "..."
                  : usage?.isLimited
                    ? usage?.monthlyLimit ?? "-"
                    : "Unlimited"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Remaining
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {usageLoading
                  ? "..."
                  : usage?.isLimited
                    ? usage?.remaining ?? 0
                    : "Unlimited"}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle2">Send Outbound Fax</Typography>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            sx={{ alignItems: { xs: "stretch", md: "flex-start" } }}
          >
            <TextField
              fullWidth
              label="To Fax Number"
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              disabled={isDisabled || sending || uploading || !hasPremiumAccess}
              sx={{ flex: { md: "0 0 32%" }, minWidth: { md: 220 } }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              sx={{ flex: { md: 1 }, minWidth: 0 }}
            >
              <TextField
                fullWidth
                label="Document URL (optional)"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                disabled={
                  isDisabled ||
                  sending ||
                  uploading ||
                  !hasPremiumAccess ||
                  Boolean(attachedFile?.fileName)
                }
                helperText={
                  attachedFile?.fileName
                    ? "Auto-filled from selected file. Clear file first to enter a URL."
                    : "Optional if file is attached. You can also paste a public URL."
                }
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Button
                variant="outlined"
                onClick={handleSelectFile}
                disabled={isDisabled || sending || uploading || !hasPremiumAccess}
                sx={{ minWidth: { xs: "100%", sm: 132 }, height: 56, flexShrink: 0 }}
              >
                {uploading ? "Uploading..." : "Upload File"}
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
            <TextField
              fullWidth
              label="File Name (optional)"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              disabled={isDisabled || sending || uploading || !hasPremiumAccess}
            />
            <TextField
              fullWidth
              label="Subject (optional)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isDisabled || sending || uploading || !hasPremiumAccess}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tif,.tiff,image/*"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
            <Typography variant="caption" color="text.secondary">
              Accepted: PDF, TIFF, JPG, PNG
            </Typography>
            {attachedFile?.fileName && (
              <>
                <Typography variant="caption" color="success.main">
                  Attached: {attachedFile.fileName}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setAttachedFile(null);
                    setFileUrl("");
                  }}
                  disabled={isDisabled || sending || uploading || !hasPremiumAccess}
                >
                  Clear File
                </Button>
              </>
            )}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Business fax number: {faxNumber || "Not configured"}
            </Typography>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={
                isDisabled || sending || uploading || !faxNumber || !hasPremiumAccess
              }
            >
              {sending ? "Sending..." : "Send Fax"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Direction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Pages</TableCell>
              <TableCell>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No fax records yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.direction}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.fromNumber || "-"}</TableCell>
                  <TableCell>{row.toNumber || "-"}</TableCell>
                  <TableCell>
                    {row.fileUrl ? (
                      <a href={row.fileUrl} target="_blank" rel="noreferrer">
                        {row.fileName || "Open"}
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{row.pages ?? "-"}</TableCell>
                  <TableCell>{toLocalTime(row.receivedAt || row.sentAt || row.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
